import { getFeatureFlagsHeaderValue } from '@/lib/utils/featureFlags';

import { get, post } from '../client';
import { B2B_API } from '../urls';

import type {
  SiteConfigResponse,
  Page,
  PageDetails,
  Widget,
  UserData,
} from '../types';
import type { AccessScope } from '@/lib/auth';

import type { UUID } from '@/types';

type ScopeOpts = { scope?: AccessScope; locale?: string };

const withTenant = (tenantId?: string): HeadersInit =>
  tenantId ? { 'X-TENANT-ID': tenantId } : {};

export const fetchSiteConfig = async (
  tenantId?: string,
): Promise<SiteConfigResponse> => {
  return get<SiteConfigResponse>(B2B_API.siteConfig, {
    headers: withTenant(tenantId),
    auth: 'none',
    next: { revalidate: 300 },
  });
};

export const fetchSitePages = async (
  siteId: UUID | undefined,
  tenantId: string,
  opts: ScopeOpts = {},
): Promise<Page[]> => {
  const { scope = 'public', locale } = opts;
  const url = locale
    ? `${B2B_API.sitePages(siteId)}?lang=${locale}`
    : B2B_API.sitePages(siteId);

  return get<Page[]>(url, {
    headers: withTenant(tenantId),
    auth: scope === 'private' ? 'auto' : 'none',
    ...(scope !== 'private' && { next: { revalidate: 300 } }),
    ...(locale && { locale }),
  });
};

export const fetchPageDetails = async (
  siteId: UUID | undefined,
  pageId: number | undefined,
  tenantId: string,
  opts: ScopeOpts = {},
): Promise<PageDetails> => {
  const { scope = 'public', locale } = opts;

  const headers: HeadersInit = {
    ...withTenant(tenantId),
  };

  const flagsValue = getFeatureFlagsHeaderValue(tenantId);
  if (flagsValue) {
    // keep as-is (string)
    (headers as Record<string, string>)['X-Feature-Flags'] = flagsValue;
  }

  const pageUrl = locale
    ? `${B2B_API.sitePageDetails(siteId, pageId)}?lang=${locale}`
    : B2B_API.sitePageDetails(siteId, pageId);

  return get<PageDetails>(pageUrl, {
    headers,
    auth: scope === 'private' ? 'auto' : 'none',
    ...(scope !== 'private' && { next: { revalidate: 60 } }),
    ...(locale && { locale }),
  });
};

export const fetchWidgetDetails = async (
  siteId: UUID | undefined,
  widgetId: number | undefined,
  tenantId: string,
  opts: ScopeOpts = {},
): Promise<Widget> => {
  const { scope = 'public', locale } = opts;

  const widgetUrl = locale
    ? `${B2B_API.siteWidgetDetails(siteId, widgetId)}?lang=${locale}`
    : B2B_API.siteWidgetDetails(siteId, widgetId);

  return get<Widget>(widgetUrl, {
    headers: withTenant(tenantId),
    auth: scope === 'private' ? 'auto' : 'none',
    ...(locale && { locale }),
  });
};

export const fetchClientDetails = async (
  siteId: UUID | undefined,
  tenantId: string,
): Promise<UserData> => {
  // this is user data => require auth
  return get<UserData>(B2B_API.clientDetails(siteId), {
    headers: withTenant(tenantId),
    auth: 'required',
  });
};

export const postStream = async (
  siteId: UUID | undefined,
  tenantId: string,
  body: unknown,
): Promise<void> => {
  await post<void>(B2B_API.streams(siteId), body, {
    headers: withTenant(tenantId),
    auth: 'auto',
  });
};

export const postStreamEvent = async (
  siteId: UUID | undefined,
  tenantId: string,
  streamId: UUID | undefined,
  body: unknown,
): Promise<void> => {
  await post<void>(B2B_API.streamEvents(siteId, streamId), body, {
    headers: withTenant(tenantId),
    auth: 'auto',
  });
};
